import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { toast } from "sonner";

function priorityBg(p) {
  if (p === "high") return "bg-amber-500/5 border-amber-500/10";
  if (p === "medium") return "bg-zinc-800/30 border-zinc-800/50";
  return "bg-zinc-800/20 border-zinc-800/30";
}

function priorityBadge(p) {
  if (p === "high") return "bg-amber-500/10 text-amber-400 border-amber-500/20";
  if (p === "medium") return "bg-blue-500/10 text-blue-400 border-blue-500/20";
  return "bg-zinc-700/50 text-zinc-400 border-zinc-600";
}

export default function AiRecommendations() {
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleGet = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/recommendations", { count: 5 });
      setRecs(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to get recommendations");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-5 shadow-[0_4px_20px_rgba(0,0,0,0.3)]">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-semibold text-zinc-100 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-400" /> AI Recommendations
          <span className="text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full font-medium">GPT-5.2</span>
        </h2>
        <Button
          data-testid="ai-recommendations-btn"
          onClick={handleGet}
          disabled={loading}
          size="sm"
          className="bg-gradient-to-r from-amber-600 to-rose-600 hover:from-amber-500 hover:to-rose-500 text-white border-0 shadow-[0_0_12px_rgba(245,158,11,0.2)]"
        >
          {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Thinking...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Get Suggestions</>}
        </Button>
      </div>
      {recs ? (
        <div className="space-y-2">
          {recs.recommendations.map((rec, i) => (
            <div
              key={rec.release_id || `ai-rec-${i}`}
              onClick={() => rec.release_id && navigate(`/catalog/${rec.release_id}`)}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${rec.release_id ? "cursor-pointer hover:border-zinc-700" : ""} ${priorityBg(rec.priority)}`}
            >
              <span className={`text-xs font-bold px-2 py-1 rounded-full border ${priorityBadge(rec.priority)}`}>
                {rec.priority?.toUpperCase()}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-zinc-200 truncate font-medium">{rec.release_name}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">{rec.reason}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {rec.is_chase && <span className="text-[9px] bg-amber-500/10 text-amber-400 px-1.5 py-0.5 rounded-full font-bold">CHASE</span>}
                {rec.is_blkltd && <span className="text-[9px] bg-purple-500/10 text-purple-400 px-1.5 py-0.5 rounded-full font-bold">BLKLTD</span>}
                <span className="font-mono text-xs text-rose-500">{rec.estimated_market_value != null ? `$${rec.estimated_market_value}` : "TBD"}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-zinc-500 text-sm">Click &quot;Get Suggestions&quot; for AI-powered collecting advice based on your collection</p>
      )}
    </div>
  );
}
